/**
 * useFileBrowserFolderContents Hook
 *
 * React Query hook for fetching folder contents in the file browser.
 * Uses React Query caching with staleTime derived from backend cache settings.
 *
 * Supports:
 * - Automatic caching per folder
 * - Prefetching for smooth navigation
 * - Manual refresh to bypass all caches
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import { useCacheSettings } from './useCacheSettings';
import type { FolderItem } from '../types/sharepoint';

/**
 * Query key factory for folder contents
 */
export const folderContentsKeys = {
  all: ['folderContents'] as const,
  tenant: (tenantId: string) => ['folderContents', tenantId] as const,
  site: (tenantId: string, siteId: string) =>
    ['folderContents', tenantId, siteId] as const,
  folder: (tenantId: string, siteId: string, folderId: string) =>
    ['folderContents', tenantId, siteId, folderId] as const,
};

/**
 * Default stale time in milliseconds (5 minutes)
 */
const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Hook return type
 */
export interface UseFileBrowserFolderContentsResult {
  items: FolderItem[];
  folders: FolderItem[];
  files: FolderItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook options
 */
interface UseFileBrowserFolderContentsOptions {
  tenantId: string;
  siteId: string;
  folderId: string;
  enabled?: boolean;
}

/**
 * React Query hook for fetching folder contents.
 *
 * Features:
 * - Automatic caching with React Query
 * - Stale time derived from backend cache settings (folderChildren TTL)
 * - Separates folders and files for easy tree/grid rendering
 * - Manual refresh bypasses both frontend and backend caches
 *
 * @param options.tenantId - The managed tenant ID
 * @param options.siteId - The SharePoint site ID
 * @param options.folderId - The folder ID (use "root" for root folder)
 * @param options.enabled - Whether to fetch (default: true)
 * @returns Folder contents, loading states, and refetch function
 */
export function useFileBrowserFolderContents({
  tenantId,
  siteId,
  folderId,
  enabled = true,
}: UseFileBrowserFolderContentsOptions): UseFileBrowserFolderContentsResult {
  const queryClient = useQueryClient();

  // Get cache settings to derive staleTime
  const { settings: cacheSettings } = useCacheSettings();

  // Calculate staleTime from backend TTL (convert seconds to ms)
  // Use slightly shorter time than backend to ensure smooth refresh
  const staleTimeMs = cacheSettings?.ttlSeconds?.folderChildren
    ? (cacheSettings.ttlSeconds.folderChildren - 30) * 1000 // 30 seconds shorter
    : DEFAULT_STALE_TIME_MS;

  const queryKey = folderContentsKeys.folder(tenantId, siteId, folderId);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      return TenantApiService.getFolderContents(tenantId, siteId, folderId, false);
    },
    enabled: enabled && !!tenantId && !!siteId && !!folderId,
    staleTime: staleTimeMs,
  });

  // Manual refetch bypasses backend cache
  const refetch = useCallback(async () => {
    // Fetch with refresh=true to bypass backend cache
    const freshData = await TenantApiService.getFolderContents(
      tenantId,
      siteId,
      folderId,
      true
    );
    // Update the cache with fresh data
    queryClient.setQueryData(queryKey, freshData);
  }, [tenantId, siteId, folderId, queryClient, queryKey]);

  // Separate folders and files, sort alphabetically by name
  const rawItems = data ?? [];
  const folders = rawItems
    .filter((item) => item.isFolder)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const files = rawItems
    .filter((item) => !item.isFolder)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  // Combined items: folders first (sorted), then files (sorted)
  const items = [...folders, ...files];

  return {
    items,
    folders,
    files,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Hook for prefetching folder contents.
 *
 * Useful for prefetching child folders when hovering over a tree node.
 */
export function usePrefetchFolderContents() {
  const queryClient = useQueryClient();
  const { settings: cacheSettings } = useCacheSettings();

  const staleTimeMs = cacheSettings?.ttlSeconds?.folderChildren
    ? (cacheSettings.ttlSeconds.folderChildren - 30) * 1000
    : DEFAULT_STALE_TIME_MS;

  const prefetch = useCallback(
    async (tenantId: string, siteId: string, folderId: string) => {
      await queryClient.prefetchQuery({
        queryKey: folderContentsKeys.folder(tenantId, siteId, folderId),
        queryFn: () =>
          TenantApiService.getFolderContents(tenantId, siteId, folderId, false),
        staleTime: staleTimeMs,
      });
    },
    [queryClient, staleTimeMs]
  );

  return { prefetch };
}

/**
 * Hook for invalidating folder contents cache.
 *
 * Useful after file operations (upload, delete, rename).
 */
export function useInvalidateFolderContents() {
  const queryClient = useQueryClient();

  const invalidateFolder = useCallback(
    async (tenantId: string, siteId: string, folderId: string) => {
      await queryClient.invalidateQueries({
        queryKey: folderContentsKeys.folder(tenantId, siteId, folderId),
      });
    },
    [queryClient]
  );

  const invalidateSite = useCallback(
    async (tenantId: string, siteId: string) => {
      await queryClient.invalidateQueries({
        queryKey: folderContentsKeys.site(tenantId, siteId),
      });
    },
    [queryClient]
  );

  const invalidateAll = useCallback(
    async (tenantId: string) => {
      await queryClient.invalidateQueries({
        queryKey: folderContentsKeys.tenant(tenantId),
      });
    },
    [queryClient]
  );

  return { invalidateFolder, invalidateSite, invalidateAll };
}
